import React, { useState, createContext, useContext } from 'react';
import { Person } from '../types';
import { seedData } from '../data/seedData';
import { logAuditEvent } from './auditLog';
interface UserContextType {
  currentUser: Person;
  role: 'Org Admin' | 'Group Admin' | 'Account Admin' | 'Account Member';
  accessibleAccountIds: string[];
  switchUser: (userId: string) => void;
  hasPermission: (permission: Permission) => boolean;
}
type Permission = 'view_all_accounts' | 'manage_users' | 'sync_spaces' | 'edit_accounts' | 'view_internal_spaces';
const UserContext = createContext<UserContextType | undefined>(undefined);
// Define mock user profiles with their accessible accounts
const userAccessMap: Record<string, {
  accountIds: string[];
  permissions: Permission[];
}> = {
  u6: {
    // Maya Singh - Org Admin (Super Admin - sees ALL accounts)
    accountIds: ['*'],
    permissions: ['view_all_accounts', 'manage_users', 'sync_spaces', 'edit_accounts', 'view_internal_spaces']
  },
  u1: {
    // Olivia Park - Group Admin
    accountIds: ['acct-hearst', 'acct-kohls', 'acct-genmills', 'acct-discover', 'acct-hilton', 'acct-cvs', 'acct-quad-internal'],
    permissions: ['view_all_accounts', 'sync_spaces', 'edit_accounts', 'view_internal_spaces']
  },
  u2: {
    // Merritt Lane - Account Admin (Kohls)
    accountIds: ['acct-kohls', 'acct-quad-internal'],
    permissions: ['manage_users', 'edit_accounts', 'view_internal_spaces', 'sync_spaces']
  },
  u3: {
    // Ava Brooks - Account Member (General Mills)
    accountIds: ['acct-genmills', 'acct-quad-internal'],
    permissions: ['view_internal_spaces']
  }
};
export function UserContextProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currentUserId, setCurrentUserId] = useState('u6'); // Default to Org Admin
  const currentUser = seedData.users.find(u => u.id === currentUserId) || seedData.users[0];
  const accessData = userAccessMap[currentUserId] || userAccessMap.u6;
  // Handle special '*' marker for all accounts
  const accessibleAccountIds = accessData.accountIds[0] === '*' ? seedData.accounts.map(acc => acc.id) : accessData.accountIds;
  const switchUser = (userId: string) => {
    const newUser = seedData.users.find(u => u.id === userId);
    if (newUser) {
      setCurrentUserId(userId);
      logAuditEvent('user_switch', `Switched to ${newUser.name}`, 'System');
    }
  };
  const hasPermission = (permission: Permission): boolean => {
    return accessData.permissions.includes(permission);
  };
  return <UserContext.Provider value={{
    currentUser,
    role: currentUser.role as UserContextType['role'],
    accessibleAccountIds,
    switchUser,
    hasPermission
  }} data-id="element-1541">
      {children}
    </UserContext.Provider>;
}
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserContextProvider');
  }
  return context;
}